import React from 'react';
import { supabase } from '../../lib/supabaseClient';

const REMEMBERED_EMAILS_KEY = 'autopilot.rememberedEmails';
const MAX_REMEMBERED_EMAILS = 5;

const loadRememberedEmails = () => {
  try {
    const raw = localStorage.getItem(REMEMBERED_EMAILS_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed.filter(e => typeof e === 'string') : [];
  } catch {
    return [];
  }
};

const rememberEmail = (newEmail) => {
  const normalized = newEmail.trim().toLowerCase();
  if (!normalized) return;
  try {
    const existing = loadRememberedEmails().filter(e => e !== normalized);
    const updated = [normalized, ...existing].slice(0, MAX_REMEMBERED_EMAILS);
    localStorage.setItem(REMEMBERED_EMAILS_KEY, JSON.stringify(updated));
  } catch {
    // localStorage unavailable (private mode / disabled) — non-fatal.
  }
};

export const LoginScreen = () => {
  const [email, setEmail]       = React.useState('');
  const [password, setPassword] = React.useState('');
  const [showPassword, setShowPassword] = React.useState(false);
  const [error, setError]       = React.useState('');
  const [loading, setLoading]   = React.useState(false);
  const [remembered]   = React.useState(loadRememberedEmails);
  const [showDropdown, setShowDropdown] = React.useState(false);
  const [resetLoading, setResetLoading] = React.useState(false);
  const [resetMsg, setResetMsg] = React.useState('');

  const handleLogin = async () => {
    if (!email || !password) {
      setError('Email and password are required.');
      return;
    }
    setLoading(true);
    setError('');
    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message || 'Incorrect email or password.');
      setLoading(false);
      return;
    }
    rememberEmail(email);
    // On success the onAuthStateChange listener in useClientId fires and the
    // dashboard re-renders automatically. Leave loading=true so the button
    // stays disabled during the transition.
  };

  // Kick off the existing password-recovery flow. Reuses the email already
  // typed above rather than adding a separate screen. Supabase appends
  // `#access_token=...&type=recovery` to redirectTo; landing back on the app
  // root, useClientId reads that hash and SetPasswordScreen takes over.
  const handleForgotPassword = async () => {
    const target = email.trim();
    if (!target) {
      setResetMsg('');
      setError('Enter your email above, then tap “Forgot password?”');
      return;
    }
    setResetLoading(true);
    setError('');
    setResetMsg('');
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(target, {
      redirectTo: window.location.origin,
    });
    setResetLoading(false);
    if (resetError) {
      setError(resetError.message || 'Could not send the reset email. Please try again.');
      return;
    }
    // Supabase returns success even for unknown emails (no account enumeration),
    // so the message is intentionally neutral.
    setResetMsg('If an account exists for that email, a reset link is on its way.');
  };

  const pickEmail = (picked) => {
    setEmail(picked);
    setShowDropdown(false);
  };

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Barlow:wght@400;500&family=Barlow+Condensed:wght@600;700&display=swap');
        .ls-wrap {
          min-height: 100vh;
          display: flex;
          align-items: center;
          justify-content: center;
          background: #0d0f14;
          font-family: 'Barlow', sans-serif;
        }
        .ls-card {
          background: rgba(255,255,255,0.04);
          border: 1px solid rgba(49,220,186,0.2);
          border-radius: 20px;
          padding: 52px 44px;
          width: 100%;
          max-width: 420px;
          text-align: center;
          box-shadow: 0 0 60px rgba(49,220,186,0.06), 0 24px 48px rgba(0,0,0,0.4);
          backdrop-filter: blur(12px);
        }
        .ls-logo-wrap {
          position: relative;
          display: inline-block;
          margin-bottom: 32px;
        }
        .ls-logo-glow {
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
          width: 140px;
          height: 140px;
          background: radial-gradient(circle, rgba(49,220,186,0.25) 0%, transparent 70%);
          border-radius: 50%;
          pointer-events: none;
        }
        .ls-logo-img {
          width: 240px;
          height: 240px;
          object-fit: contain;
          position: relative;
          z-index: 1;
          filter: drop-shadow(0 0 12px rgba(49,220,186,0.5));
        }
        .ls-title {
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 32px;
          font-weight: 700;
          color: #fff;
          margin: 0 0 8px;
          letter-spacing: 0.02em;
        }
        .ls-sub {
          color: rgba(255,255,255,0.4);
          font-size: 14px;
          margin: 0 0 36px;
        }
        .ls-input {
          width: 100%;
          padding: 14px 18px;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.1);
          border-radius: 10px;
          color: #fff;
          font-family: 'Barlow', sans-serif;
          font-size: 16px;
          margin-bottom: 14px;
          box-sizing: border-box;
          transition: border-color 0.2s;
          outline: none;
        }
        .ls-input:focus { border-color: #31DCBA; }
        .ls-input::placeholder { color: rgba(255,255,255,0.25); }
        .ls-email-field { position: relative; }
        .ls-password-field { position: relative; }
        /* Keep typed text clear of the eye toggle. */
        .ls-input--password { padding-right: 48px; }
        .ls-eye-btn {
          position: absolute;
          top: 0;
          /* .ls-input has margin-bottom:14px; span exactly the input box so the
             icon centres on the field regardless of its rendered height. */
          bottom: 14px;
          right: 0;
          width: 48px;
          display: flex;
          align-items: center;
          justify-content: center;
          background: none;
          border: none;
          padding: 0;
          cursor: pointer;
          color: rgba(255,255,255,0.4);
          transition: color 0.2s;
        }
        .ls-eye-btn:hover { color: #31DCBA; }
        .ls-eye-btn:focus-visible { color: #31DCBA; outline: none; }
        .ls-dropdown {
          position: absolute;
          top: calc(100% - 8px);
          left: 0;
          right: 0;
          background: #15181f;
          border: 1px solid rgba(49,220,186,0.25);
          border-radius: 10px;
          overflow: hidden;
          z-index: 10;
          box-shadow: 0 12px 28px rgba(0,0,0,0.5);
        }
        .ls-dropdown-item {
          display: block;
          width: 100%;
          padding: 12px 18px;
          background: none;
          border: none;
          border-bottom: 1px solid rgba(255,255,255,0.05);
          color: rgba(255,255,255,0.75);
          font-family: 'Barlow', sans-serif;
          font-size: 15px;
          text-align: left;
          cursor: pointer;
          transition: background 0.15s, color 0.15s;
        }
        .ls-dropdown-item:last-child { border-bottom: none; }
        .ls-dropdown-item:hover { background: rgba(49,220,186,0.1); color: #fff; }
        .ls-btn {
          width: 100%;
          padding: 14px;
          background: #31DCBA;
          color: #0d0f14;
          font-family: 'Barlow Condensed', sans-serif;
          font-size: 18px;
          font-weight: 700;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          border: none;
          border-radius: 10px;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.1s;
          margin-top: 6px;
        }
        .ls-btn:hover { opacity: 0.9; transform: translateY(-1px); }
        .ls-btn:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
        .ls-error {
          color: #ff6b6b;
          font-size: 13px;
          margin-top: 14px;
        }
        .ls-notice {
          color: #31DCBA;
          font-size: 13px;
          margin-top: 14px;
        }
        .ls-forgot-row {
          display: flex;
          justify-content: flex-end;
          margin: -4px 0 6px;
        }
        .ls-forgot {
          background: none;
          border: none;
          padding: 4px 2px;
          color: rgba(255,255,255,0.45);
          font-family: 'Barlow', sans-serif;
          font-size: 13px;
          cursor: pointer;
          transition: color 0.2s;
        }
        .ls-forgot:hover:not(:disabled) { color: #31DCBA; }
        .ls-forgot:disabled { opacity: 0.5; cursor: not-allowed; }
        .ls-footer {
          margin-top: 32px;
          color: rgba(255,255,255,0.15);
          font-size: 12px;
          letter-spacing: 0.08em;
          text-transform: uppercase;
        }
      `}</style>
      <div className="ls-wrap">
        <div className="ls-card">
          <div className="ls-logo-wrap">
            <div className="ls-logo-glow" />
            <img src="/autopilot_logo.png" alt="Autopilot Digital" className="ls-logo-img" />
          </div>
          <h2 className="ls-title">Welcome Back</h2>
          <p className="ls-sub">Sign in to access your dashboard</p>
          <div className="ls-email-field">
            <input
              className="ls-input"
              type="email"
              autoComplete="email"
              placeholder="Email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onFocus={() => setShowDropdown(true)}
              onBlur={() => setShowDropdown(false)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
            {showDropdown && (() => {
              const matches = remembered.filter(
                e => e !== email.trim().toLowerCase() && e.includes(email.trim().toLowerCase())
              );
              return matches.length > 0 ? (
                <div className="ls-dropdown">
                  {matches.map(e => (
                    <button
                      key={e}
                      type="button"
                      className="ls-dropdown-item"
                      // onMouseDown fires before the input's onBlur, so the
                      // dropdown is still open when the click is handled.
                      onMouseDown={() => pickEmail(e)}
                    >
                      {e}
                    </button>
                  ))}
                </div>
              ) : null;
            })()}
          </div>
          <div className="ls-password-field">
            <input
              className="ls-input ls-input--password"
              type={showPassword ? 'text' : 'password'}
              autoComplete="current-password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && handleLogin()}
            />
            <button
              type="button"
              className="ls-eye-btn"
              onClick={() => setShowPassword(s => !s)}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              aria-pressed={showPassword}
              // Skip in tab order so keyboard users flow Email → Password → Enter.
              tabIndex={-1}
            >
              {showPassword ? (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-7 11-7 11 7 11 7-4 7-11 7-11-7-11-7z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
          <div className="ls-forgot-row">
            <button
              type="button"
              className="ls-forgot"
              onClick={handleForgotPassword}
              disabled={resetLoading || loading}
            >
              {resetLoading ? 'Sending…' : 'Forgot password?'}
            </button>
          </div>
          <button className="ls-btn" onClick={handleLogin} disabled={loading}>
            {loading ? 'Signing in…' : 'Enter Dashboard'}
          </button>
          {error && <div className="ls-error">{error}</div>}
          {resetMsg && <div className="ls-notice">{resetMsg}</div>}
          <div className="ls-footer">Secure Client Access</div>
        </div>
      </div>
    </>
  );
};
